/**
 * 클라이언트 측 이미지 리사이즈/압축 유틸 (리뷰 사진 → `review-images` 직행 업로드).
 *
 * 폰 카메라 원본(3~8MB)을 업로드 전에 WebP 로 줄여서 업로드 시간·저장 용량을
 * 줄인다. 서버 업로드 경로의 prepareStorageImage 와 같은 규칙:
 * 최대 너비 1600px(확대 없음, 비율 유지), 품질 0.8.
 *
 * - `createImageBitmap`의 `imageOrientation: 'from-image'` 옵션으로 EXIF 회전을
 *   자동 반영하므로 Canvas에 그릴 때 별도 회전 처리가 필요 없다.
 * - 브라우저가 WebP 인코딩을 못 하면(구형 Safari 는 PNG 로 폴백) 같은 품질의
 *   JPEG 로 저장한다.
 * - 압축 후 오히려 커지면 원본 그대로 반환.
 */
export async function compressReviewImage(
  file: File,
  opts: { maxWidth?: number; quality?: number } = {},
): Promise<File> {
  const { maxWidth = 1600, quality = 0.8 } = opts;

  // 이미지가 아니거나 브라우저가 API를 지원하지 않으면 원본 반환
  if (!file.type.startsWith("image/")) return file;
  if (typeof createImageBitmap !== "function") return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    return file;
  }

  let { width, height } = bitmap;
  if (width > maxWidth) {
    height = Math.round((height * maxWidth) / width);
    width = maxWidth;
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close();
    return file;
  }

  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const encode = (type: string) =>
    new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));
  let blob = await encode("image/webp");
  // Trust blob.type: a browser that cannot encode WebP silently returns PNG.
  if (!blob || blob.type !== "image/webp") blob = await encode("image/jpeg");

  if (!blob || blob.size >= file.size) return file;

  const type = blob.type === "image/webp" ? "image/webp" : "image/jpeg";
  const ext = type === "image/webp" ? ".webp" : ".jpg";
  const newName = file.name.replace(/\.[^.]+$/, "") + ext;
  return new File([blob], newName, {
    type,
    lastModified: Date.now(),
  });
}
